// IE Hub - Theme Provider
// Manages theme state and provides theme context

import React, { createContext, useContext, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@ui/index';
import { mergeTokens } from '@tokens/resolvers/mergeTokens.ts';
import { themeOverrides as defaultTheme } from '@config/theme';

interface ThemeContextType {
  theme: any;
  client: string;
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

// Theme Context
const ThemeContext = createContext<ThemeContextType>({
  theme: null,
  client: 'default'
});

export const useThemeContext = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

function ThemeProvider({ children }: ThemeProviderProps) {
  // Get client from environment
  const client = (import.meta as any).env?.VITE_CLIENT || 'default';

  // Create light-only theme synchronously
  const theme = useMemo(() => {
    try {
      // Use the client theme overrides imported via @config alias
      return mergeTokens(defaultTheme);
    } catch (error) {
      console.error('Error creating theme:', error);
      return mergeTokens();
    }
  }, []);

  const contextValue: ThemeContextType = {
    theme,
    client
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export default ThemeProvider;
